import React, { useCallback, useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Row,
  Typography,
  Modal,
  message,
  Segmented,
  Card,
  Col,
} from "antd";
import { debounce } from "lodash";
import {
  InstagramOutlined,
  TikTokOutlined,
  PlusCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import WeekHoursField from "pages/collab/WeekHoursField";
import { Venue } from "interfaces/venue";
import { CustomerPaymentMethod } from "interfaces/customer-payment-method";
import { useDispatch, useSelector } from "react-redux";
import {
  BusinessState,
  getBusinessInfo,
  getBusinessSubscription,
} from "redux-context/business";
import {
  getPaymentMethods,
  getPlacesByName,
  updateVenue,
} from "services/SauceService";
import { getBrandCardImage, hasBrandCardImage } from "helpers/brand-cards";
import { ReactComponent as OpenTableIcon } from "assets/images/open-table-icon.svg";

interface LocationFormProps {
  selectedVenue?: Venue;
  onCloseForm: (saved?: boolean) => void;
}

const LocationForm: React.FC<LocationFormProps> = ({
  selectedVenue,
  onCloseForm,
}) => {
  const dispatch = useDispatch<any>();
  const [form] = Form.useForm();
  const googlePlaceId = Form.useWatch("googlePlaceId", form);

  const [places, setPlaces] = useState<any[]>([]);
  const { business } = useSelector((state: BusinessState) => state.business);
  const [paymentMethods, setPaymentMethods] = useState<CustomerPaymentMethod[]>(
    []
  );
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("venue");

  const getBusinessPaymentMethods = useCallback(async () => {
    if (paymentMethods.length > 0) return;
    const params = {
      businessId: business._id,
    };
    try {
      const response: any = await getPaymentMethods(params);

      if (response?.paymentMethods?.length > 0) {
        setPaymentMethods(response.paymentMethods);
        const paymentMethods: CustomerPaymentMethod[] =
          response?.paymentMethods;

        if (!selectedVenue?.preferredPayment) {
          form.setFieldValue("preferredPayment", paymentMethods[0].id);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    business._id,
    paymentMethods.length,
    form,
    selectedVenue?.preferredPayment,
  ]);

  const getLocation = useCallback(async (locationName: string) => {
    setLoadingLocation(true);
    const response: any = await getPlacesByName({ name: locationName });
    setPlaces(
      response.map((place: any) => ({
        value: place.place_id,
        label: place.name,
        ...place,
      }))
    );
    setLoadingLocation(false);
  }, []);

  const onFinish = async () => {
    const save = async () => {
      setLoading(true);
      const businessId = business._id;
      const values = form.getFieldsValue(true);
      const params = {
        businessId,
        venueId: selectedVenue?._id,
        ...values,
      };
      try {
        const response: any = await updateVenue(params);
        if (response.message) {
          dispatch(getBusinessInfo());
          dispatch(getBusinessSubscription());
          message.success("Venue saved successfully");

          onCloseForm(true);
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to save venue");
      }
      setLoading(false);
    };

    Modal.confirm({
      title:
        "IMPORTANT: Please note that the changes will only affect new collaborations. All existing collaborations with this location will retain the old information.",
      icon: "",
      onOk: async () => {
        save();
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const handleSearch = (newValue: string) => {
    if (newValue) getLocation(newValue);
  };

  const handleChange = (_: string, option: any) => {
    form.setFieldValue("addressDisplay", option.formatted_address);
    form.setFieldValue("name", option.name);
  };

  useEffect(() => {
    if (selectedVenue)
      setPlaces([
        {
          value: selectedVenue?.googlePlaceId,
          label: selectedVenue?.name,
          name: selectedVenue?.name,
          formatted_address: selectedVenue?.addressDisplay,
        },
      ]);
  }, [selectedVenue]);

  useEffect(() => {
    getBusinessPaymentMethods();
  }, [getBusinessPaymentMethods]);

  const tabOptions = [
    { value: "venue", label: "Venue Info" },
    { value: "contacts", label: "Users" },
    { value: "social", label: "Social & Hours" },
    ...(paymentMethods.length > 0
      ? [{ value: "payment", label: "Payment" }]
      : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "venue":
        return (
          <>
            <Typography.Title
              level={4}
              style={{ paddingBottom: 8, display: "block" }}
            >
              Venue Information
            </Typography.Title>
            <Form.Item label="Find location" name="googlePlaceId">
              <Select
                placeholder="Typing the name and address"
                showSearch
                filterOption={false}
                onSearch={debounce(handleSearch, 500)}
                onSelect={handleChange}
                options={places}
                loading={loadingLocation}
                allowClear
              />
            </Form.Item>
            <Form.Item label="Name" name="name" hidden={!googlePlaceId} noStyle>
              <Input placeholder="Name of the location" />
            </Form.Item>
            <div
              style={{
                backgroundColor: "#F9F5ED",
                marginBottom: 24,
                height: 40,
                marginTop: -4,
                borderRadius: "0 0 6px 6px",
                display: googlePlaceId ? "flex" : "none",
                alignItems: "center",
                border: "1px solid #E6E6E6",
                padding: "8px 16px",
                gap: 8,
              }}
            >
              <Form.Item name="ghostKitchen" noStyle>
                <Switch />
              </Form.Item>
              Ghost kitchen
            </div>
          </>
        );
      case "contacts":
        return (
          <>
            <Typography.Text style={{ paddingBottom: 8, display: "block" }}>
              User Info
            </Typography.Text>
            <Form.List name="pocs">
              {(fields, { add, remove }) => (
                <div
                  style={{
                    display: "flex",
                    rowGap: 16,
                    flexDirection: "column",
                    marginBottom: 32,
                  }}
                >
                  {fields.map((field, index) => (
                    <div key={`poc_${field.name}`}>
                      {index > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: 8,
                          }}
                        >
                          <CloseOutlined
                            onClick={() => {
                              remove(field.name);
                            }}
                          />
                        </div>
                      )}
                      <Form.Item name={[field.name, "name"]}>
                        <Input placeholder="First name" />
                      </Form.Item>
                      <Form.Item name={[field.name, "email"]} noStyle>
                        <Input type="email" placeholder="Email" />
                      </Form.Item>
                      <div
                        style={{
                          backgroundColor: "#F9F5ED",
                          marginBottom: 24,
                          height: 40,
                          marginTop: -4,
                          borderRadius: "0 0 6px 6px",
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #E6E6E6",
                          padding: "8px 16px",
                          gap: 8,
                        }}
                      >
                        <Form.Item
                          name={[field.name, "notifyEmail"]}
                          noStyle
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                        Send login information to email
                      </div>
                      <Form.Item name={[field.name, "phone"]} noStyle>
                        <Input placeholder="Phone number" />
                      </Form.Item>
                      <div
                        style={{
                          backgroundColor: "#F9F5ED",
                          marginBottom: 24,
                          height: 40,
                          marginTop: -4,
                          borderRadius: "0 0 6px 6px",
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #E6E6E6",
                          padding: "8px 16px",
                          gap: 8,
                        }}
                      >
                        <Form.Item
                          name={[field.name, "includeCollabs"]}
                          noStyle
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                        Include in notifications about collabs
                      </div>
                    </div>
                  ))}

                  <span
                    style={{ color: "#1D2023", cursor: "pointer" }}
                    onClick={() => add()}
                  >
                    <PlusCircleOutlined style={{ marginRight: 4 }} />
                    Add another User
                  </span>
                </div>
              )}
            </Form.List>
          </>
        );
      case "social":
        return (
          <>
            <Form.Item label="Links to social media" name="instagramUrl">
              <Input placeholder="Instagram" prefix={<InstagramOutlined />} />
            </Form.Item>
            <Form.Item name="tiktokUrl">
              <Input placeholder="TikTok" prefix={<TikTokOutlined />} />
            </Form.Item>
            <Form.Item
              name="openTableUrl"
              rules={[
                {
                  validator: (_, value) => {
                    const re =
                      /^https:\/\/www\.opentable\.com\/booking\/restref\/availability\?(?=.*\bcorrelationId=[a-f0-9-]{36}\b)(?=.*\brestRef=\d+\b).*/i;

                    if (!value) return Promise.resolve();
                    return re.test(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error("Invalid OpenTable URL"));
                  },
                },
              ]}
            >
              <Input placeholder="Open Table" prefix={<OpenTableIcon />} />
            </Form.Item>
            <Typography.Text style={{ paddingBottom: 8, display: "block" }}>
              Working Hours
            </Typography.Text>
            <WeekHoursField name="venueWorkingHours" />
          </>
        );
      case "payment":
        return (
          <>
            {paymentMethods.length > 0 && (
              <Form.Item name="preferredPayment" label="Payment method">
                <Select
                  placeholder="Select payment method"
                  style={{ width: "100%" }}
                >
                  {paymentMethods.map((method) => (
                    <Select.Option key={method.id} value={method.id}>
                      {method.type === "card" ? (
                        hasBrandCardImage(method?.card?.brand) ? (
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <img
                              src={getBrandCardImage(method?.card?.brand)}
                              alt={method?.card?.brand}
                              style={{ height: 16 }}
                            />
                            <span style={{ marginLeft: 8 }}>
                              {method?.card?.last4}
                            </span>
                          </span>
                        ) : (
                          `${method?.card?.brand} - ${method?.card?.last4}`
                        )
                      ) : method.type === "us_bank_account" ? (
                        `${method?.us_bank_account?.bank_name} - ${method?.us_bank_account?.last4}`
                      ) : (
                        "Unknown payment method"
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={selectedVenue}
      onFinish={onFinish}
      style={{ minWidth: 450 }}
    >
      <Row>
        <Col xs={24}>
          <Segmented
            size="large"
            options={tabOptions}
            value={activeTab}
            onChange={setActiveTab}
            style={{
              borderRadius: "8px 8px 0 0 ",

              marginBottom: 0,
            }}
            className="collab-segmented segmented-mobile"
          />
        </Col>
        <Col xs={24}>
          <Card style={{ borderRadius: "0 0 8px 8px" }}>
            <Col xs={24}>{renderTabContent()}</Col>
            <Col xs={24}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
              <Button
                htmlType="button"
                type="text"
                onClick={() => onCloseForm(false)}
              >
                Cancel
              </Button>
            </Col>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};

export default LocationForm;
